/* eslint-disable no-underscore-dangle */
/* eslint-disable class-methods-use-this */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { CertificateValidationInterface } from "@ajsprivate/acrobatjs";
import {
    VALIDATOR_NAME,
    VALIDATION_TIME,
    TRUST_SOURCE,
    HTTP_ERROR_STRINGS,
    VALIDATION_STATUS,
} from "../../utils/constants";

class ExternalCertificateValidator extends CertificateValidationInterface {
    constructor(externalCertificateValidator) {
        super();
        this.externalCertificateValidator = externalCertificateValidator || null;
    }

    name() {
        return VALIDATOR_NAME.ExternalValidator;
    }

    _base64ToArrayBuffer(certChain = []) {
        if (!certChain.length) {
            return null;
        }

        let decodedString = "";
        certChain.forEach(cert => {
            decodedString += window.atob(cert);
        });

        const len = decodedString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i += 1) {
            bytes[i] = decodedString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    validate(certificateChain) {
        // eslint-disable-next-line consistent-return
        return new Promise(async (resolve, reject) => {
            if (!Array.isArray(certificateChain)) {
                return reject(new Error("certificateChain must be an array"));
            }

            if (!this.externalCertificateValidator) {
                return reject(new Error("ExternalCertificateValidator is not available"));
            }

            try {
                // if ExternalCertificateValidator doesn't return response within 5 sec
                // reject the API call
                setTimeout(() => reject(new Error(HTTP_ERROR_STRINGS.apiTimeoutError)), 5000);

                const buffer = this._base64ToArrayBuffer(certificateChain);
                const result = await this.externalCertificateValidator({ buffer });

                if (!result || !result.status) {
                    return reject(new Error("ExternalCertificateValidator empty response"));
                }

                if (result.status === "NOTVALID") {
                    result.status = VALIDATION_STATUS.invalid;
                }
                if (!result.certificates) {
                    result.certificates = certificateChain;
                }
                if (!result.trustSources && result.status === VALIDATION_STATUS.valid) {
                    result.trustSources = [TRUST_SOURCE.OS];
                }
                if (!result.validation) {
                    result.validation = {
                        validatedOn: VALIDATION_TIME.currentTime,
                        timestamp: new Date().toISOString(),
                    };
                }

                resolve(result);
            } catch (err) {
                reject(err);
            }
        });
    }
}

export default ExternalCertificateValidator;
